/**
 * CustomerContextTab — pestaña "Cliente y origen" del panel 360.
 * Consume GET /customers/<id>/recent-documents/ (Fase 1): situación financiera
 * (saldo / vencido / crédito disponible) + últimos documentos del cliente.
 */
import { useQuery } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { Loader2, AlertTriangle, ArrowRight } from 'lucide-react'
import apiClient from '@/lib/apiClient'
import { fmtDate } from '@/lib/utils'

// Cuántos documentos mostramos en el panel; el resto se ve en el listado filtrado.
const RECENT_LIMIT = 6

interface RecentDoc {
  id: string
  document_type: string
  folio: string | null
  status: string
  collection_status: string
  total: string
  outstanding_amount: string
  issue_date: string | null
  due_date: string | null
  days_overdue: number
}
interface RecentDocsResponse {
  customer: { id: string; legal_name: string; tax_id: string; payment_term_days: number; credit_limit: string }
  summary: { outstanding_total: string; overdue_total: string; credit_limit: string; credit_available: string | null }
  documents: RecentDoc[]
}

export function CustomerContextTab({
  customerId,
  fmt,
  locale,
  onNavigate,
  onViewCustomer,
}: {
  customerId: string
  fmt: (v: string | number) => string
  locale: string
  /** Navegar a un documento del cliente dentro del mismo 360. */
  onNavigate?: (docId: string) => void
  /** Abrir la ficha del cliente en el drawer (solo en el panel; no se pasa
   *  cuando este componente ya se renderiza DENTRO del drawer). */
  onViewCustomer?: () => void
}) {
  const navigate = useNavigate()
  const { data, isLoading, isError } = useQuery<RecentDocsResponse>({
    queryKey: ['customer-recent-documents', customerId, RECENT_LIMIT],
    queryFn: async () => {
      // Pedimos 1 de más para saber si hay más documentos que los mostrados.
      const res = await apiClient.get(`/sales/customers/${customerId}/recent-documents/`, {
        params: { limit: RECENT_LIMIT + 1 },
      })
      return res.data
    },
    enabled: !!customerId,
  })

  if (isLoading) return <div className="grid place-items-center py-10 text-content-muted"><Loader2 className="animate-spin" /></div>
  if (isError || !data) return <p className="text-content-muted text-sm py-6 text-center">No se pudo cargar la información del cliente.</p>

  const { customer, summary, documents } = data
  const overdue = Number(summary.overdue_total) > 0
  const shown = documents.slice(0, RECENT_LIMIT)
  const hasMore = documents.length > RECENT_LIMIT
  const goToAllDocuments = () =>
    navigate(`/sales/documents?customer_id=${customer.id}&customer_name=${encodeURIComponent(customer.legal_name)}`)

  return (
    <div className="space-y-4">
      <div>
        <p className="font-bold text-content">{customer.legal_name}</p>
        <p className="text-[12.5px] text-content-muted">{customer.tax_id}</p>
        {onViewCustomer && (
          <button
            type="button"
            onClick={onViewCustomer}
            className="mt-1.5 inline-flex items-center gap-1 text-[12.5px] font-semibold text-primary hover:underline"
          >
            Ver ficha del cliente <ArrowRight size={13} />
          </button>
        )}
      </div>

      {/* Situación financiera: una fila por monto (mejor lectura en el panel angosto). */}
      <div className="flex flex-col gap-1.5">
        <Stat label="Saldo por cobrar" value={fmt(summary.outstanding_total)} />
        <Stat label="Vencido" value={fmt(summary.overdue_total)} danger={overdue} />
        <Stat label="Crédito disp." value={summary.credit_available != null ? fmt(summary.credit_available) : 'Sin límite'} />
      </div>

      {overdue && (
        <div className="flex items-center gap-2 bg-warning/15 text-warning rounded-lg px-3 py-2 text-[12.5px]">
          <AlertTriangle size={15} /> Este cliente tiene saldo vencido.
        </div>
      )}

      <div>
        <p className="text-[11px] font-extrabold uppercase tracking-wide text-content-subtle mb-2">Últimos documentos</p>
        <div className="flex flex-col gap-1.5">
          {shown.length === 0 && <p className="text-content-subtle text-[12.5px]">Sin documentos.</p>}
          {shown.map((d) => (
            <button
              key={d.id}
              type="button"
              onClick={() => onNavigate?.(d.id)}
              disabled={!onNavigate}
              className="flex items-center justify-between gap-2.5 border border-divider rounded-lg px-2.5 py-2 w-full text-left enabled:hover:bg-surface-muted enabled:hover:border-divider-strong disabled:cursor-default"
            >

              <div className="min-w-0">
                <div className="text-[12.5px] font-semibold">{d.document_type} {d.folio || '—'}</div>
                <div className="text-[11.5px] text-content-subtle">{d.issue_date ? fmtDate(d.issue_date, locale) : '—'}</div>
              </div>
              <div className="text-right">
                <div className="text-[12.5px] font-bold tabular-nums">{fmt(d.total)}</div>
                {Number(d.outstanding_amount) > 0 && (
                  <div className={`text-[11px] ${d.days_overdue > 0 ? 'text-danger' : 'text-content-subtle'}`}>
                    Saldo {fmt(d.outstanding_amount)}
                  </div>
                )}
              </div>
            </button>
          ))}
        </div>
        {(hasMore || shown.length > 0) && (
          <button
            type="button"
            onClick={goToAllDocuments}
            className="mt-2 inline-flex items-center gap-1.5 text-[12.5px] font-semibold text-primary hover:underline"
          >
            Ver todos los documentos del cliente
            <ArrowRight size={14} />
          </button>
        )}
      </div>
    </div>
  )
}

function Stat({ label, value, danger }: { label: string; value: string; danger?: boolean }) {
  return (
    <div className="flex items-center justify-between gap-3 bg-soft rounded-lg px-3 py-2">
      <span className="text-[11px] uppercase tracking-wide text-content-subtle">{label}</span>
      <span className={`text-[14px] font-bold tabular-nums ${danger ? 'text-danger' : 'text-content'}`}>{value}</span>
    </div>
  )
}
